import { ChamberLabel } from "@logora/parliament.parliament_chamber.chamber_label";
import { MemberBox } from "@logora/parliament.parliament_member.member_box";
import { Label } from "@logora/parliament.ui.label";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { DossierIcon } from "@logora/parliament.icons.regular_icons";
import {
	Box,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	TextField,
} from "@mui/material";
import cx from "classnames";
import React, { useState, useEffect } from "react";
import { useDataProvider } from "react-admin";
import { useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import classes from "./SearchBar.module.scss";

export const SearchBar = ({
	onSearchChange,
	placeholder,
	styles,
    className,
	size = "normal",
	onUpdateGlobalFilters,
	disableSyncWithLocation = false,
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [searchQuery, setSearchQuery] = useState(
		searchParams.get("query") || "",
	);
	const [showCompletions, setShowCompletions] = useState(false);
	const [selectedCompletions, setSelectedCompletions] = useState([]);
	const [suggestions, setSuggestions] = useState([]);
	const [suggestionType, setSuggestionType] = useState(null);
	const location = useLocation();
	const COMPLETION_LENGTH = size === "small" ? 45 : 85;
	const specialSymbols = {
		"@": {
			model: "acteurs",
			key: "acteurRefUid",
		},
		"/": {
			model: "organes",
			key: "organeRefUid",
		},
		"#": {
			model: "dossiers",
			key: "dossierRefUid",
		}
	};
	const [loading, setLoading] = useState(false);
	const dataProvider = useDataProvider();

	useEffect(() => {
		const prefixCheck = Object.keys(specialSymbols).some((s) =>
			searchQuery.startsWith(s),
		);
		setShowCompletions(prefixCheck && suggestions.length > 0);
	}, [suggestions, searchQuery]);

	useEffect(() => {
		if (Object.keys(specialSymbols).some((s) => searchQuery.startsWith(s))) {
			const type = specialSymbols[searchQuery.slice(0, 1)] || null;
			setSuggestionType(type);
		}
	}, [searchQuery]);

	useEffect(() => {
		const fetchSuggestions = async () => {
			if (suggestionType && searchQuery.length > 1) {
				setLoading(true);
				try {
					const { data } = await dataProvider.getList(suggestionType.model, {
						search: searchQuery.slice(1),
						filter: { actif: true, prefixSearch: true },
						pagination: { page: 1, perPage: 10 },
					});
					setSuggestions(data);
				} catch (error) {
					console.error(error);
				} finally {
					setLoading(false);
				}
			} else {
				setSuggestions([]);
			}
		};
		fetchSuggestions();
	}, [searchQuery, dataProvider, suggestionType]);

	useEffect(() => {
		const fetchRecords = async () => {
			const params = new URLSearchParams(location.search);
			const acteurRef = params.get("acteurRefUid");
			const organeRef = params.get("organeRefUid");
			const dossierRef = params.get("dossierRefUid");
			
			if (acteurRef) {
				try {
					const response = await dataProvider.getOne("acteurs", {
						id: acteurRef,
					});
					const record = response.data;
					handleCompletionClick(record, {
						model: "acteurs",
						key: "acteurRefUid",
					});
				} catch (error) {
					console.log(error);
				}
			}
			if (organeRef) {
				try {
					const response = await dataProvider.getOne("organes", {
						id: organeRef,
					});
					const record = response.data;
					handleCompletionClick(record, {
						model: "organes",
						key: "organeRefUid",
					});
				} catch (error) {
					console.log(error);
				}
			}
			if (dossierRef) {
				try {
					const response = await dataProvider.getOne("dossiers", {
						id: dossierRef,
					});
					const record = response.data;
					handleCompletionClick(record, {
						model: "dossiers",
						key: "dossierRefUid",
					});
				} catch (error) {
					console.log(error);
				}
			}
		};

		fetchRecords();
	}, [location.search]);

	/*
	useEffect(() => {
        const query = searchParams.get("query");
        if (query !== searchQuery) {
            setSearchQuery(query || "");
        }
    }, [searchParams]);
	*/

	const handleSearchChange = (e) => {
		setSearchQuery(e.target.value);
		if (e.target.value === "") {
			searchParams.delete("query");
			!disableSyncWithLocation && setSearchParams(searchParams);
		}
	};

	const handleResetSearch = (e) => {
		setSearchQuery("");
		setShowCompletions(false);
		setSelectedCompletions([]);
		searchParams.delete("query");
		!disableSyncWithLocation && setSearchParams(searchParams);
		onSearchChange("");
		onUpdateGlobalFilters?.([]);
	};

	const handleSearchSubmit = () => {
		if (!searchQuery) {
			searchParams.delete("query");
		}
		onSearchChange(searchQuery.trim());
		!disableSyncWithLocation &&
			setSearchParams((params) => {
				if (searchQuery) {
					params.set("query", searchQuery);
				}
				return params;
			});
	};

	const handleKeyDown = (e) => {
		if (e.keyCode === 13) {
			handleSearchSubmit();
		}
	};

	const handleCompletionClick = (completion, manualType) => {
		const parsedCompletion = {
			filter: {
				[suggestionType ? suggestionType.key : manualType.key]: completion.uid,
			},
			object: completion,
			model: suggestionType ? suggestionType.model : manualType.model,
		};

		setSelectedCompletions((prevSelectedCompletions) => {
			const newSelections = prevSelectedCompletions.filter(
				(c) => c.model !== parsedCompletion.model,
			);
			const updatedSelections = [...newSelections, parsedCompletion];
			onUpdateGlobalFilters?.(updatedSelections);
			return updatedSelections;
		});

		!disableSyncWithLocation &&
			setSearchParams((params) => {
				if (parsedCompletion) {
					params.set(
						`${Object.keys(parsedCompletion.filter)[0]}`,
						Object.values(parsedCompletion.filter)[0],
					);
				}
				return params;
			});

		setSearchQuery(searchParams.get("query") || "");
	};

	const clearFilter = (indexToRemove) => {
		const newSelectedCompletions = selectedCompletions.filter(
			(_, index) => index !== indexToRemove,
		);
		searchParams.delete(Object.keys(selectedCompletions[indexToRemove].filter));
		!disableSyncWithLocation && setSearchParams(searchParams);
		setSelectedCompletions(newSelectedCompletions);
		onUpdateGlobalFilters?.(newSelectedCompletions);
	};

	const renderLabelFilter = (completion) => {
		if (!completion) {
			return null;
		}
		switch (completion.model) {
			case "acteurs":
				return (
					<MemberBox
						firstName={completion.object?.prenom}
						lastName={completion.object?.nom}
						imageUrl={completion.object?.urlImage}
						size="small"
						layout="oneLine"
					/>
				);
			case "organes":
				return (
					<Box className={classes.completionBox}>
						{completion.object?.chambre && (
							<>
								<ChamberLabel
									short
									chamber={completion.object?.chambre}
									className={classes.searchLabel}
								/>
								<Box variant="span" className={classes.searchLabel}>
									·
								</Box>
							</>
						)}
						<Label
							className={cx(classes.searchLabel, classes.organeSearchLabel)}
							text={`${completion.object?.libelle.slice(0, COMPLETION_LENGTH)} ${
								completion.object?.libelle.length > COMPLETION_LENGTH ? "..." : ""
							}`}
							style={{ whiteSpace: "wrap" }}
						/>
					</Box>
				);
			case "dossiers":
				return (
					<Box className={classes.completionBox}>
						<Label short text="Dossier" leftIcon={<DossierIcon width={20} height={20} />} className={classes.searchLabel} />
						<Box variant="span" className={classes.searchLabel}>
							·
						</Box>
						<Label
							className={cx(classes.searchLabel, classes.organeSearchLabel)}
							text={`${completion.object.titre.slice(0, COMPLETION_LENGTH)} ${
								completion.object.titre.length > COMPLETION_LENGTH ? "..." : ""
							}`}
							style={{ whiteSpace: "wrap" }}
						/>
					</Box>
				)
			default:
				return completion.object?.libelle;
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				position: "relative",
				width: "100%",
				...styles,
			}}
      		className={className}
		>
			<div
				className={cx(classes.searchContainer, {
					[classes.large]: size !== "small",
					[classes.small]: size === "small",
				})}
			>
				<div className={classes.completionContainer}>
					{selectedCompletions.map((completion, index) => (
						<Box
							key={index}
							className={classes.searchFilter}
							sx={{ margin: "4px 8px 4px 0", display: "inline-flex" }}
						>
							{renderLabelFilter(completion)}
							<IconButton
								size="small"
								onClick={() => clearFilter(index)}
								sx={{ padding: 0 }}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Box>
					))}
				</div>
				<div className={classes.inputContainer}>
					<TextField
						variant="standard"
						placeholder={placeholder}
						value={searchQuery}
						onChange={handleSearchChange}
						onKeyDown={handleKeyDown}
						size={size}
						InputProps={{
							disableUnderline: true,
							className: classes.searchField,
						}}
						sx={{ flex: 1 }}
					/>
					<IconButton onClick={handleResetSearch} sx={{ marginLeft: "8px" }}>
						{searchQuery.length === 0 && selectedCompletions.length === 0 ? (
							<SearchIcon />
						) : (
							<CloseIcon />
						)}
					</IconButton>
				</div>
			</div>
			{showCompletions && !loading && (
				<List
					sx={{
						border: "1px solid rgba(0, 0, 0, 0.2)",
						borderRadius: 1,
						bgcolor: "background.paper",
						position: "absolute",
						width: size === "small" ? "200%" : "100%",
						marginLeft: size === "small" ? "-100%" : 0,
						top: "100%",
						zIndex: "99999",
						overflow: "hidden",
					}}
				>
					{suggestions.map((suggestion, index) => (
						<ListItem key={index}>
							<ListItemButton onClick={() => handleCompletionClick(suggestion)}>
								{(() => {
									switch (suggestionType?.model) {
										case "acteurs":
											return (
												<MemberBox
													firstName={suggestion.prenom}
													lastName={suggestion.nom}
													imageUrl={suggestion.urlImage}
													size="small"
													layout="oneLine"
												/>
											);
										case "organes":
											return (
												<Box className={classes.completionBox} title={suggestion.libelle}>
													{suggestion.chambre && (
														<>
															<ChamberLabel short chamber={suggestion.chambre} />
															<Box variant="span" className={classes.searchLabel}>
																·
															</Box>
														</>
													)}
													<Label
														text={`${suggestion.libelle.slice(0, COMPLETION_LENGTH)} ${
															suggestion.libelle.length > COMPLETION_LENGTH ? "..." : ""
														}`}
														className={classes.completionLabel}
													/>
												</Box>
											);
										case "dossiers":
											return (
												<Box className={classes.completionBox} title={suggestion.titre}>
													<Label short text="Dossier" leftIcon={<DossierIcon width={20} height={20} />} />
													<Box variant="span" className={classes.searchLabel}>
														·
													</Box>
													<Label
														text={`${suggestion.titre.slice(0, COMPLETION_LENGTH)} ${
															suggestion.titre.length > COMPLETION_LENGTH ? "..." : ""
														}`}
														className={classes.completionLabel}
													/>
												</Box>
											)
										default:
											return null;
									}
								})()}
							</ListItemButton>
						</ListItem>
					))}
				</List>
			)}
		</Box>
	);
};
